import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/use-currency";
import { format } from "date-fns";
import { Loader2, Plus, RefreshCw, Trash2, Download, Gift, Copy, User } from "lucide-react";
import html2canvas from "html2canvas";

interface Salesperson {
    id: string;
    name: string;
    email: string;
}

interface ReferralCode {
    id: string;
    code: string;
    salesperson_id: string;
    salespeople?: Salesperson;
    discount_type: string;
    discount_value: number;
    max_discount_amount: number | null;
    min_order_amount: number;
    commission_type: string;
    commission_value: number;
    start_date: string | null;
    end_date: string | null;
    total_usage_limit: number | null;
    per_user_limit: number;
    current_usage_count: number;
    is_active: boolean;
    created_at: string;
}

export function ReferralCodesManager() {
    const [codes, setCodes] = useState<ReferralCode[]>([]);
    const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { toast } = useToast();
    const { hasPermission } = useAuth();
    const { formatPrice } = useCurrency();

    const [formData, setFormData] = useState({
        code: "",
        salesperson_id: "",
        discount_type: "PERCENTAGE",
        discount_value: "",
        max_discount_amount: "",
        min_order_amount: "0",
        commission_type: "PERCENTAGE",
        commission_value: "",
        start_date: "",
        end_date: "",
        total_usage_limit: "",
        per_user_limit: "1",
        is_active: true
    });

    const [previewCode, setPreviewCode] = useState<ReferralCode | null>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const [brandName, setBrandName] = useState("Amadeo Photography");
    const [redemptions, setRedemptions] = useState<Record<string, any[]>>({});

    useEffect(() => {
        fetchSalespeople();
        fetchCodes();
        fetchBrandName();
    }, []);

    const fetchBrandName = async () => {
        const { data } = await supabase.from('photographer_profiles').select('display_name').limit(1).single();
        if (data?.display_name) {
            setBrandName(data.display_name);
        }
    };

    const fetchSalespeople = async () => {
        const { data } = await supabase
            .from('salespeople')
            .select('id, name, email')
            .eq('is_active', true)
            .order('name');

        setSalespeople(data || []);
    };

    const fetchCodes = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('referral_codes')
            .select('*, salespeople(id, name, email)')
            .order('created_at', { ascending: false });

        if (data) {
            // Fetch redemptions for each code
            const redemptionsMap: Record<string, any[]> = {};
            for (const code of data) {
                const { data: redemptionData } = await supabase
                    .from('referral_code_redemptions')
                    .select('*, bookings(customer_name, customer_email, created_at)')
                    .eq('referral_code_id', code.id)
                    .order('redeemed_at', { ascending: false });

                redemptionsMap[code.id] = redemptionData || [];
            }
            setRedemptions(redemptionsMap);
        }

        if (error) {
            toast({
                title: "Error fetching codes",
                description: error.message,
                variant: "destructive"
            });
        } else {
            setCodes(data || []);
        }
        setLoading(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const generateRandomCode = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let result = "";
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setFormData(prev => ({ ...prev, code: result }));
    };

    const handleSubmit = async () => {
        if (!formData.code || !formData.salesperson_id || !formData.discount_value || !formData.commission_value) {
            toast({
                title: "Validation Error",
                description: "Code, Salesperson, Discount, and Commission are required",
                variant: "destructive"
            });
            return;
        }

        const payload = {
            code: formData.code.toUpperCase(),
            salesperson_id: formData.salesperson_id,
            discount_type: formData.discount_type,
            discount_value: parseFloat(formData.discount_value),
            max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
            min_order_amount: parseFloat(formData.min_order_amount) || 0,
            commission_type: formData.commission_type,
            commission_value: parseFloat(formData.commission_value),
            start_date: formData.start_date || null,
            end_date: formData.end_date || null,
            total_usage_limit: formData.total_usage_limit ? parseInt(formData.total_usage_limit) : null,
            per_user_limit: parseInt(formData.per_user_limit) || 1,
            is_active: formData.is_active
        };

        // Validate date range
        if (payload.start_date && payload.end_date) {
            const startDate = new Date(payload.start_date);
            const endDate = new Date(payload.end_date);
            if (startDate >= endDate) {
                toast({
                    title: "Invalid Date Range",
                    description: "Start date must be before end date",
                    variant: "destructive"
                });
                return;
            }
        }

        const { error } = await supabase.from('referral_codes').insert([payload]);

        if (error) {
            toast({ title: "Error creating code", description: error.message, variant: "destructive" });
        } else {
            toast({ title: "Success", description: "Referral code created successfully" });
            setIsDialogOpen(false);
            fetchCodes();
            // Reset form
            setFormData({
                code: "",
                salesperson_id: "",
                discount_type: "PERCENTAGE",
                discount_value: "",
                max_discount_amount: "",
                min_order_amount: "0",
                commission_type: "PERCENTAGE",
                commission_value: "",
                start_date: "",
                end_date: "",
                total_usage_limit: "",
                per_user_limit: "1",
                is_active: true
            });
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        const { error } = await supabase
            .from('referral_codes')
            .update({ is_active: !currentStatus })
            .eq('id', id);

        if (!error) fetchCodes();
    };

    const deleteCode = async (id: string) => {
        if (!confirm("Are you sure you want to delete this referral code?")) return;

        const { error } = await supabase
            .from('referral_codes')
            .delete()
            .eq('id', id);

        if (error) {
            toast({
                title: "Error",
                description: "Could not delete (used codes cannot be deleted)",
                variant: "destructive"
            });
        } else {
            fetchCodes();
        }
    };

    const handleDownloadImage = async (code: ReferralCode) => {
        setPreviewCode(code);
        setTimeout(async () => {
            if (cardRef.current) {
                try {
                    const canvas = await html2canvas(cardRef.current, {
                        backgroundColor: null,
                        scale: 2
                    });
                    const link = document.createElement('a');
                    link.download = `REFERRAL-${code.code}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                    toast({ title: "Downloaded", description: "Referral card image saved." });
                } catch (err) {
                    console.error(err);
                    toast({ title: "Error", description: "Failed to generate image", variant: "destructive" });
                }
            }
        }, 500);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Referral Codes</h2>
                    <p className="text-muted-foreground">Manage sales referral codes and track commissions.</p>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button><Plus className="mr-2 h-4 w-4" /> Create Referral Code</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Create Referral Code</DialogTitle>
                            <DialogDescription>Set up a referral code for a salesperson with discount and commission rules.</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-4 py-4">
                            {/* Code */}
                            <div className="space-y-2 col-span-2 sm:col-span-1">
                                <Label>Referral Code *</Label>
                                <div className="flex gap-2">
                                    <Input
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder="e.g. JOHN2024"
                                        className="uppercase"
                                    />
                                    <Button variant="outline" size="icon" onClick={generateRandomCode} type="button">
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Salesperson */}
                            <div className="space-y-2 col-span-2 sm:col-span-1">
                                <Label>Salesperson *</Label>
                                <Select
                                    value={formData.salesperson_id}
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, salesperson_id: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select salesperson" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {salespeople.map(person => (
                                            <SelectItem key={person.id} value={person.id}>
                                                {person.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Discount Section */}
                            <div className="col-span-2 border-t pt-4">
                                <h3 className="font-semibold mb-3">Customer Discount</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Discount Type</Label>
                                        <Select
                                            value={formData.discount_type}
                                            onValueChange={(v) => setFormData(prev => ({ ...prev, discount_type: v }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                                                <SelectItem value="FIXED_AMOUNT">Fixed Amount (€)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Discount Value *</Label>
                                        <Input
                                            name="discount_value"
                                            type="number"
                                            value={formData.discount_value}
                                            onChange={handleInputChange}
                                            placeholder={formData.discount_type === 'PERCENTAGE' ? "20" : "50"}
                                        />
                                    </div>

                                    {formData.discount_type === 'PERCENTAGE' && (
                                        <div className="space-y-2">
                                            <Label>Max Discount Amount</Label>
                                            <Input
                                                name="max_discount_amount"
                                                type="number"
                                                value={formData.max_discount_amount}
                                                onChange={handleInputChange}
                                                placeholder="e.g. 100"
                                            />
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label>Min Order Amount</Label>
                                        <Input
                                            name="min_order_amount"
                                            type="number"
                                            value={formData.min_order_amount}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Commission Section */}
                            <div className="col-span-2 border-t pt-4">
                                <h3 className="font-semibold mb-3">Salesperson Commission</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Commission Type</Label>
                                        <Select
                                            value={formData.commission_type}
                                            onValueChange={(v) => setFormData(prev => ({ ...prev, commission_type: v }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                                                <SelectItem value="FIXED_AMOUNT">Fixed Amount (€)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Commission Value *</Label>
                                        <Input
                                            name="commission_value"
                                            type="number"
                                            value={formData.commission_value}
                                            onChange={handleInputChange}
                                            placeholder={formData.commission_type === 'PERCENTAGE' ? "10" : "25"}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Commission is calculated on the final price AFTER discount is applied.
                                </p>
                            </div>

                            {/* Usage Limits */}
                            <div className="space-y-2">
                                <Label>Start Date</Label>
                                <Input
                                    name="start_date"
                                    type="date"
                                    value={formData.start_date}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>End Date</Label>
                                <Input
                                    name="end_date"
                                    type="date"
                                    value={formData.end_date}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Total Usage Limit</Label>
                                <Input
                                    name="total_usage_limit"
                                    type="number"
                                    value={formData.total_usage_limit}
                                    onChange={handleInputChange}
                                    placeholder="Unlimited"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Limit Per User</Label>
                                <Input
                                    name="per_user_limit"
                                    type="number"
                                    value={formData.per_user_limit}
                                    onChange={handleInputChange}
                                    placeholder="1"
                                />
                            </div>

                            <div className="space-y-2 col-span-2">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={formData.is_active}
                                        onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_active: c }))}
                                    />
                                    <Label>Active</Label>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={handleSubmit}>Create Referral Code</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="flex justify-center p-8">
                    <Loader2 className="animate-spin h-8 w-8 text-primary" />
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {codes.map((code) => (
                        <Card key={code.id} className={`relative overflow-hidden border-l-4 ${code.is_active ? 'border-l-blue-500' : 'border-l-muted'}`}>
                            {!code.is_active && <div className="absolute inset-0 bg-background/50 z-10 pointer-events-none" />}

                            <CardHeader className="pb-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <CardTitle className="font-mono text-xl tracking-wider select-all cursor-pointer flex items-center gap-2"
                                            onClick={() => {
                                                navigator.clipboard.writeText(code.code);
                                                toast({ description: "Copied to clipboard" });
                                            }}>
                                            {code.code} <Copy className="h-3 w-3 text-muted-foreground opacity-50" />
                                        </CardTitle>
                                        <CardDescription className="flex items-center gap-1 mt-1">
                                            <User className="h-3 w-3" />
                                            {code.salespeople?.name || 'Unknown'}
                                        </CardDescription>
                                    </div>
                                    <div className="flex flex-col gap-1 items-end">
                                        <Badge variant={code.is_active ? "default" : "secondary"}>
                                            {code.is_active ? "Active" : "Inactive"}
                                        </Badge>
                                        {code.end_date && new Date(code.end_date) < new Date() && (
                                            <Badge variant="destructive" className="text-[10px]">Expired</Badge>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="text-sm space-y-2 pb-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <p className="text-muted-foreground text-xs">Discount</p>
                                        <p className="font-semibold">
                                            {code.discount_type === 'PERCENTAGE' ? `${code.discount_value}%` : formatPrice(code.discount_value)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground text-xs">Commission</p>
                                        <p className="font-semibold text-green-600">
                                            {code.commission_type === 'PERCENTAGE' ? `${code.commission_value}%` : formatPrice(code.commission_value)}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Used:</span>
                                    <span className="font-medium">{code.current_usage_count} / {code.total_usage_limit || "∞"}</span>
                                </div>

                                {redemptions[code.id] && redemptions[code.id].length > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Total Commission:</span>
                                        <span className="font-medium text-green-600">
                                            {formatPrice(redemptions[code.id].reduce((sum, r) => sum + (r.commission_amount || 0), 0))}
                                        </span>
                                    </div>
                                )}

                                {(code.start_date || code.end_date) && (
                                    <div className="text-xs text-muted-foreground mt-2">
                                        {code.start_date && <>From: {format(new Date(code.start_date), 'dd MMM yyyy')}<br /></>}
                                        {code.end_date && <>Expires: {format(new Date(code.end_date), 'dd MMM yyyy')}</>}
                                    </div>
                                )}
                            </CardContent>

                            <CardFooter className="pt-2 flex justify-end gap-2 border-t bg-muted/20">
                                <Button variant="ghost" size="sm" onClick={() => toggleStatus(code.id, code.is_active)}>
                                    {code.is_active ? "Deactivate" : "Activate"}
                                </Button>

                                {redemptions[code.id] && redemptions[code.id].length > 0 && (
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button variant="outline" size="sm">
                                                View Redemptions ({redemptions[code.id].length})
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                            <DialogHeader>
                                                <DialogTitle>Redemptions for {code.code}</DialogTitle>
                                                <DialogDescription>
                                                    Total commission generated: {formatPrice(redemptions[code.id].reduce((sum, r) => sum + (r.commission_amount || 0), 0))}
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-2 mt-4">
                                                {redemptions[code.id].map((redemption: any) => (
                                                    <div key={redemption.id} className="border rounded-lg p-3 text-sm bg-card">
                                                        <div className="flex justify-between items-start">
                                                            <div className="space-y-1">
                                                                <p className="font-semibold text-foreground">
                                                                    {redemption.bookings?.customer_name || 'Unknown Client'}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                                    <Copy className="h-3 w-3" /> {redemption.customer_email}
                                                                </p>
                                                                <div className="flex gap-2 mt-2">
                                                                    <Badge variant="outline" className="text-[10px]">
                                                                        Sale: {formatPrice(redemption.final_amount)}
                                                                    </Badge>
                                                                    <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                                                                        Commission: {formatPrice(redemption.commission_amount)}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                            <div className="text-right flex flex-col justify-between h-full">
                                                                <p className="text-xs text-muted-foreground font-medium">
                                                                    {format(new Date(redemption.redeemed_at), 'dd MMM yyyy')}
                                                                </p>
                                                                <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-tighter">
                                                                    {format(new Date(redemption.redeemed_at), 'hh:mm a')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                )}

                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm" onClick={() => setPreviewCode(code)}>
                                            <Download className="h-4 w-4 mr-1" /> Card
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-md">
                                        <DialogHeader>
                                            <DialogTitle>Download Referral Card</DialogTitle>
                                        </DialogHeader>
                                        <div className="flex justify-center py-6 bg-gray-100 rounded-lg overflow-hidden">
                                            <div
                                                ref={cardRef}
                                                className="w-[350px] aspect-[1.58/1] bg-gradient-to-br from-blue-900 to-blue-950 text-white p-6 pb-8 rounded-xl shadow-2xl relative flex flex-col justify-between overflow-hidden"
                                            >
                                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
                                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-2xl -ml-10 -mb-10 pointer-events-none" />

                                                <div className="flex items-center gap-3 z-10">
                                                    <Gift className="h-8 w-8 text-blue-300" />
                                                    <div>
                                                        <h3 className="font-bold text-lg leading-none tracking-wide text-gray-100">REFERRAL CODE</h3>
                                                        <p className="text-[10px] text-gray-400 uppercase tracking-widest">{brandName}</p>
                                                    </div>
                                                </div>

                                                <div className="text-center z-10 my-2">
                                                    <div className="text-5xl font-bold tracking-tighter text-blue-300 drop-shadow-sm">
                                                        {previewCode?.discount_type === 'PERCENTAGE' ? `${previewCode.discount_value}%` : `€${previewCode?.discount_value}`}
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-300 tracking-[0.2em] uppercase mt-1">OFF YOUR NEXT SESSION</div>
                                                </div>

                                                <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-center border border-white/10 z-10">
                                                    <p className="text-[10px] text-gray-400 mb-1">USE CODE AT CHECKOUT</p>
                                                    <p className="text-2xl font-mono font-bold tracking-widest text-white">{previewCode?.code}</p>
                                                </div>

                                                <div className="z-10 flex flex-col items-center gap-1">
                                                    <div className="text-xs text-gray-300 flex items-center gap-1">
                                                        <User className="h-3 w-3" />
                                                        Referred by: {previewCode?.salespeople?.name}
                                                    </div>
                                                    {previewCode?.end_date ? (
                                                        <div className="text-[10px] font-bold text-blue-300 uppercase tracking-[0.2em] bg-blue-500/10 py-1.5 px-4 rounded-full border border-blue-500/20 shadow-inner">
                                                            VALID TILL {format(new Date(previewCode.end_date), 'dd MMM yyyy')}
                                                        </div>
                                                    ) : (
                                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                                                            NO EXPIRY
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button onClick={() => handleDownloadImage(previewCode!)} className="w-full">
                                                <Download className="mr-2 h-4 w-4" /> Download PNG
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>

                                {hasPermission('delete_referral_codes') && (
                                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => deleteCode(code.id)}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </CardFooter>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
